<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { api, ApiCallError } from '@/api/client';
import { useUserStore } from '@/stores/user';
import { haptic, openExternalLink } from '@/composables/useTelegramWebApp';
import MxCard from '@/components/MxCard.vue';
import MxChip from '@/components/MxChip.vue';
import MxSkeleton from '@/components/MxSkeleton.vue';
import type { CreatePaymentOut, Package } from '@/api/types';

const packages = ref<Package[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const userStore = useUserStore();
const { me } = storeToRefs(userStore);
const router = useRouter();

onMounted(async () => {
  try {
    packages.value = await api.get<Package[]>('/payments/packages');
  } catch (e) {
    error.value = e instanceof ApiCallError ? e.message : 'Не удалось получить пакеты';
  } finally {
    loading.value = false;
  }
});

async function pick(pkg: Package) {
  haptic.impact('medium');
  try {
    const out = await api.post<CreatePaymentOut>('/payments/prodamus/create', {
      package_key: pkg.key,
    });
    // Open Prodamus checkout in the system browser — keeps the Mini App
    // alive in Telegram so the BackButton stays under our control. The
    // success page polls /me until the webhook credits tokens.
    openExternalLink(out.checkout_url);
    await router.push('/topup/success');
  } catch (e) {
    error.value = e instanceof ApiCallError ? e.message : 'Не удалось создать платёж';
    haptic.notification('error');
  }
}
</script>

<template>
  <section class="topup">
    <h1>Пополнить баланс</h1>
    <p class="bal" v-if="me">Текущий баланс: <b>{{ me.balance_tokens }}</b> т.</p>

    <div v-if="loading" class="list">
      <MxSkeleton v-for="n in 4" :key="n" height="80px" radius="var(--r-md)" />
    </div>
    <div v-else-if="error" class="alert">{{ error }}</div>
    <ul v-else class="list">
      <li v-for="p in packages" :key="p.key">
        <MxCard
          :class="['pkg', { 'pkg--popular': p.is_popular }]"
          @click="pick(p)"
        >
          <div class="pkg__main">
            <p class="pkg__title">{{ p.label }}</p>
            <p class="pkg__sub">{{ p.tokens }} токенов</p>
          </div>
          <div class="pkg__right">
            <p class="pkg__price">{{ p.price_rub }} ₽</p>
            <MxChip v-if="p.discount_pct > 0" variant="accent">−{{ p.discount_pct }}%</MxChip>
          </div>
        </MxCard>
      </li>
    </ul>

    <p class="hint">Оплата картой через Prodamus.</p>
  </section>
</template>

<style scoped>
.topup { padding: var(--sp-3) 0 var(--sp-8); display: flex; flex-direction: column; gap: var(--sp-4); }
.topup h1 { font-family: var(--font-display); font-size: var(--fs-2xl); }
.bal { color: var(--text-secondary); }
.list { display: flex; flex-direction: column; gap: var(--sp-2); }
.pkg { display: flex; justify-content: space-between; align-items: center; cursor: pointer; }
.pkg--popular { border: 1px solid var(--accent); }
.pkg__title { font-weight: 600; }
.pkg__sub { color: var(--text-secondary); font-size: var(--fs-sm); }
.pkg__right { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.pkg__price { font-weight: 600; font-size: var(--fs-lg); color: var(--accent); }
.hint { color: var(--text-tertiary); font-size: var(--fs-sm); text-align: center; }
.alert { color: var(--error); padding: var(--sp-3); background: var(--bg-elevated); border-radius: var(--r-md); }
</style>
